/**
 * Notification Permission Prompt Component
 * Displays a friendly prompt to request notification permissions
 * Only shows when notifications are supported and not yet granted
 */

import React, { useState, useEffect } from 'react';
import {
    isNotificationSupported,
    getNotificationPermission,
    enableNotifications
} from '../../services/notificationMessagingService';

const NotificationPermissionPrompt = ({ onClose, autoShow = true }) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [permission, setPermission] = useState('default');

    useEffect(() => {
        const checkPermission = () => {
            const currentPermission = getNotificationPermission();
            setPermission(currentPermission);

            // Auto-show if notifications are supported but not granted/denied
            if (autoShow && currentPermission === 'default' && isNotificationSupported()) {
                // Delay showing the prompt to avoid being intrusive
                const timer = setTimeout(() => {
                    setVisible(true);
                }, 3000); // Show after 3 seconds

                return () => clearTimeout(timer);
            }
        };

        checkPermission();
    }, [autoShow]);

    const handleEnableNotifications = async () => {
        setLoading(true);
        try {
            const result = await enableNotifications();

            if (result.success) {
                setVisible(false);
                if (onClose) onClose(true);
            } else {
                alert('Unable to enable notifications. Please check your browser settings.');
            }
        } catch (error) {
            console.error('Error enabling notifications:', error);
            alert('Failed to enable notifications. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = () => {
        setVisible(false);
        if (onClose) onClose(false);

        // Remember user dismissed the prompt (store in localStorage)
        localStorage.setItem('notificationPromptDismissed', Date.now().toString());
    };

    // Don't show if already granted or denied, or if user dismissed recently
    if (permission !== 'default' || !isNotificationSupported()) {
        return null;
    }

    // Check if user dismissed recently (within last 7 days)
    const dismissedTime = localStorage.getItem('notificationPromptDismissed');
    if (dismissedTime) {
        const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) {
            return null;
        }
    }

    if (!visible) {
        return null;
    }

    return (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-96 z-50 animate-in slide-in-from-bottom duration-300">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Header with close button */}
                <div className="flex items-start justify-between p-4 pb-2">
                    <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">
                                Stay Updated!
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Enable notifications
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="px-4 pb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                        Get notified about likes, comments, mentions, and new followers. Stay connected with your community!
                    </p>

                    {/* Benefits list */}
                    <ul className="space-y-2 mb-4">
                        {[
                            { icon: '❤️', text: 'Likes on your posts' },
                            { icon: '💬', text: 'New comments' },
                            { icon: '@', text: 'When you\'re mentioned' },
                            { icon: '👥', text: 'New followers' }
                        ].map((item, index) => (
                            <li key={index} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                <span className="text-base">{item.icon}</span>
                                <span>{item.text}</span>
                            </li>
                        ))}
                    </ul>

                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={handleEnableNotifications}
                            disabled={loading}
                            className="flex-1 bg-accent hover:bg-accent/90 text-white font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Enabling...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                    <span>Enable Notifications</span>
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="sm:flex-none px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm font-medium"
                        >
                            Maybe Later
                        </button>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-3 text-center">
                        You can always change this in settings
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NotificationPermissionPrompt;
